import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface AuthContextType {
  user: any;
  auth0OrgId: string | null;  // Auth0 Organization ID (e.g., org_tH6tBxLgpgHEXTZB)
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently
  } = useAuth0();

  // Extract org_id from custom claim
  const auth0OrgId = user?.['https://atiende.app/org_id'] || null;

  // Version drift is handled by UpdateAvailableBanner (polling + focus listener)
  // outside the auth window. Reloading during auth settlement raced with the
  // Auth0 callback and caused the post-login login-loop.

  const login = () => {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname },
      authorizationParams: {
        // Auth0 will show organization selector if user belongs to multiple
        prompt: 'login'
      }
    });
  };

  const logout = async () => {
    console.log('🚪 Logout function called');
    console.log('📍 Current location:', window.location.origin);
    console.log('🔐 Is authenticated before logout:', isAuthenticated);

    try {
      console.log('🧹 Clearing localStorage and sessionStorage...');
      // Clear any local storage
      localStorage.clear();
      sessionStorage.clear();
      console.log('✅ Storage cleared');

      console.log('🔓 Calling Auth0 logout...');
      // Clear Auth0 cache and logout
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
      console.log('✅ Auth0 logout completed');
    } catch (error) {
      console.error('❌ Error during logout:', error);
      console.log('🔄 Forcing logout with redirect...');
      // Force logout even if there's an error
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = window.location.origin;
    }
  };

  // Enhanced getAccessToken with better error handling and logging
  const getAccessToken = async (): Promise<string> => {
    console.log("🔑 getAccessToken called - isAuthenticated:", isAuthenticated, "isLoading:", isLoading);

    if (!isAuthenticated) {
      console.warn("⚠️ User is not authenticated, cannot get token");
      throw new Error("User is not authenticated");
    }

    if (isLoading) {
      console.warn("⏳ Auth is still loading, waiting before getting token...");
      // Wait a bit for auth to finish
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    try {
      const audience = process.env.REACT_APP_AUTH0_AUDIENCE || '';
      console.log("🔑 Requesting token with audience:", audience);

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: audience,
          scope: 'openid profile email offline_access',
        },
        cacheMode: 'on', // Better cache handling
      });

      if (token) {
        console.log("✅ Token retrieved successfully, length:", token.length);
      } else {
        console.error("❌ Token is null after retrieval");
      }

      return token;
    } catch (error: any) {
      if (
        error?.error === 'interaction_required' ||
        error?.error === 'missing_refresh_token' ||
        error?.message?.includes('Missing Refresh Token')
      ) {
        localStorage.clear();
        sessionStorage.clear();
        await auth0Logout({ logoutParams: { returnTo: window.location.origin } });
        throw error;
      }

      console.error('❌ Error getting access token:', error);
      console.error('❌ Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      auth0OrgId,
      isLoading,
      isAuthenticated,
      login,
      logout,
      getAccessToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
